import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BottomPopup from '@components/modals/BottomPopup';
import { toastSuccess } from '@components/Toast';

import ConfirmationModal from '@features/crm/components/ConfirmationModal';
import UpdateFittingModal from '@features/designTool/components/UpdateFittingModal';
import { generateBlockLibraryListParams } from '@features/filter/static/filterUtil';
import BlockItem from '@features/assets/components/blockLibrary/BlockItem';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { clearFilterResponse, handleFilter, updateGlobalFilterData } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { likeProduct, unlikeProduct } from '@store/design/action';

import { getFittingResponse } from '@services/measurementService';
import { fetchDependentFilterList } from '@services/commonService';
import { unmarkProductBlock } from '@services/assetsService';

import { isPageReachBottom, printApiError, showDesignViewPage } from '@utils/Util';

import { useDownloadTpSummaryMutation } from '@/api/commonDownloadsApi';

const CardViewItems = ({ isSetParamExist = false, setTotalElements }) => {
	const [blockIdList, setBlockIdList] = useState([]);
	const [isOpenBlock, setIsOpenBlock] = useState(false);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const dispatch = useDispatch();
	const [blockIdToView, setBlockIdToView] = useState(null);
	const [blockList, setBlockList] = useState(null);
	const [isDefaultOpenBasicInfo, setIsDefaultOpenBasicInfo] = useState(false);
	const [showBlockInfoModal, setShowBlockInfoModal] = useState(false);
	const [isBottomCategory, setIsBottomCategory] = useState(false);
	const [markProductId, setMarkProductId] = useState(null);
	const [fittingResponse, setFittingResponse] = useState(null);
	const [blockSaveType, setBlockSaveType] = useState('');
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [unmarkPorductid, setUnmarkProductId] = useState(false);
	const filterResponse = useSelector((store) => store.filterResponseStore);
	let filterfields = useSelector((store) => store.filterFieldStore);
	const [page, setPage] = useState(0);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const scrollRef = useRef({});
	const myStateRef = useRef({});
	const { ...paramsData } = generateBlockLibraryListParams(filterfields);
	const [downloadTpSummary] = useDownloadTpSummaryMutation();

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	const setMyState = (data) => {
		myStateRef.current = data;
	};

	useEffect(() => {
		if (filterResponse?.isBlockLibrary) {
			setCurrentState(filterResponse);
			setBlockList(filterResponse);
		}
	}, [filterResponse]);

	useEffect(() => {
		window.scrollTo(0, 0);
		dispatch(openLoader());
		dispatch(fetchDependentFilterList({ type: 'BLOCK_LIBRARY_CARD_VIEW_LIST' }, paramsData));
		setPage(0);
		let queryParams = generateBlockLibraryListParams(filterfields, 0, sortValue);
		dispatch(handleFilter('getAllProductBlock', queryParams, false)).finally(() => {
			dispatch(closeLoader());
		});
	}, [filterfields]);

	useEffect(() => {
		if (page !== 0) {
			dispatch(openLoader());
			let queryParams = generateBlockLibraryListParams(filterfields, page, sortValue);
			dispatch(handleFilter('getAllProductBlock', queryParams, true)).finally(() => {
				dispatch(closeLoader());
			});
		}
	}, [page]);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			dispatch(clearFilterResponse());
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	useEffect(() => {
		setMyState(blockList);
		let blockIds = blockList?.data
			?.filter((product) => product.extraFlag && showDesignViewPage(product))
			.map((product) => product.id);
		setBlockIdList(blockIds);
	}, [blockList]);

	useEffect(() => {
		if (blockList) {
			setTotalElements(blockList.totalElements);
		}
	}, [blockList, setTotalElements]);

	const onClickBlockImage = (blockId, openBasicInfo = false) => {
		setIsDefaultOpenBasicInfo(openBasicInfo);
		setBlockIdToView(blockId);
	};

	const onCloseViewModal = () => {
		setBlockIdToView(null);
		setIsDefaultOpenBasicInfo(false);
		isOpenBlock && setIsOpenBlock(false);
		dispatch(clearDesignViewModalInfo({}));
	};

	const onCloseConfirmationModal = () => {
		setOpenConfirmation(false);
	};

	useEffect(() => {
		if (blockIdToView !== null) {
			openDesignViewModal();
		}
	}, [blockIdToView]);

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: blockIdToView,
				isShowMergeTechPack: isSetParamExist,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST',
				blockIdList: blockIdList,
				loadMoreDesign: true,
				isDefaultOpenBasicInfo: isDefaultOpenBasicInfo
			})
		);
	};

	const onSetLike = (id) => {
		let productList = blockList?.data.map((item) => {
			if (item.id === id) {
				item.liked = true;
			}
			return item;
		});
		setBlockList({ ...blockList, data: [...productList] });
	};

	const onSetUnlike = (id) => {
		let productList = blockList?.data.map((item) => {
			if (item.id === id) {
				item.liked = false;
			}
			return item;
		});
		setBlockList({ ...blockList, data: [...productList] });
	};

	const onLikeProduct = async (id) => {
		onSetLike(id);
		let response = await dispatch(likeProduct(id));
		if (!response) {
			onSetUnlike(id);
		}
	};

	const onUnlikeProduct = async (id) => {
		onSetUnlike(id);
		let response = await dispatch(unlikeProduct(id));
		if (!response) {
			onSetLike(id);
		}
	};

	const renderDesignList = () => {
		if (blockList?.data?.length > 0) {
			return blockList.data.map((block, index) => {
				return (
					<BlockItem
						block={block}
						index={index}
						selectedProducts={selectedProducts}
						setSelectedProducts={setSelectedProducts}
						onClickBlockImage={onClickBlockImage}
						key={`design_${index}`}
						onLikeProduct={onLikeProduct}
						onUnlikeProduct={onUnlikeProduct}
						onMarkProduct={onMarkProduct}
						onUnmarkProduct={onUnmarkProduct}
						setOpenConfirmation={setOpenConfirmation}
						setUnmarkProductId={setUnmarkProductId}
					/>
				);
			});
		}
	};

	const clearSelection = () => {
		setSelectedProducts([]);
	};

	const renderAlertMessage = () => {
		if (blockList?.data?.length === 0) {
			return (
				<div className="alert alert-secondary text-center">
					<p>No block found</p>
				</div>
			);
		}
	};

	const downloadReportByProductIds = () => {
		downloadTpSummary({
			productIds: selectedProducts
		});
	};

	const onActionClick = async (type) => {
		switch (type) {
			case 'DOWNLOAD_REPORT':
				await downloadReportByProductIds();
				break;
			case 'CLEAR':
				clearSelection();
				break;
			default:
				break;
		}
	};

	const onMarkProduct = async (id, saveType, parentCatergory) => {
		setShowBlockInfoModal(true);
		setMarkProductId(id);
		setBlockSaveType(saveType);
		if (parentCatergory === 'BOTTOM') {
			setIsBottomCategory(true);
		} else {
			setIsBottomCategory(false);
		}
	};

	const onUnmarkProduct = async () => {
		dispatch(openLoader());
		try {
			await unmarkProductBlock(unmarkPorductid);
			const updatedDesignList = blockList.data.filter((block) => block.id !== unmarkPorductid);
			setBlockList({ ...blockList, data: updatedDesignList });
			dispatch(closeLoader());
			setOpenConfirmation(false);
			toastSuccess('Successfully updated');
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	useEffect(() => {
		if (markProductId && blockSaveType === 'edit') {
			fetchFittingResponse();
		}
	}, [markProductId, blockSaveType]);

	const fetchFittingResponse = async () => {
		if (markProductId) {
			try {
				dispatch(openLoader());
				const data = await getFittingResponse(markProductId);
				setFittingResponse(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const updateProductCardOnMark = (data) => {
		dispatch(updateGlobalFilterData(data));
	};

	const handleOnFittingUpdate = async () => {
		try {
			dispatch(openLoader());
			setShowBlockInfoModal(false);
			toastSuccess('Updated successfully');
			await fetchFittingResponse();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<>
			<div className="design-list-container">
				<div className="design-list-row">{renderDesignList()}</div>
				{renderAlertMessage()}
			</div>
			{selectedProducts?.length > 0 && (
				<BottomPopup
					length={selectedProducts?.length}
					type={'BLOCK_LIBRARY'}
					onActionClick={onActionClick}
					showUnSelectAll={selectedProducts.length === blockList?.data?.length}
				/>
			)}
			{showBlockInfoModal && (
				<UpdateFittingModal
					productId={markProductId}
					isShowClose={true}
					blockResponse={fittingResponse}
					onAddSuccess={handleOnFittingUpdate}
					onCloseModal={() => setShowBlockInfoModal(false)}
					blockSaveType={blockSaveType}
					updateProductCardOnMark={updateProductCardOnMark}
					openedFrom={'block'}
					isBottomCategory={isBottomCategory}
				/>
			)}
			{openConfirmation && (
				<ConfirmationModal
					title={'Unmark block'}
					closeModal={onCloseConfirmationModal}
					description={' about unmarking this block?'}
					callback={onUnmarkProduct}
					footerType={'CLOSE'}
				/>
			)}
		</>
	);
};

export default CardViewItems;
